/**
 * ADR 216 — what the 3D camera is currently looking at, expressed on the floor.
 *
 * The minimap draws this as the "you are here" rectangle: zoomed into one aisle,
 * the 2D map has to say WHERE that aisle is. The geometry is the four frustum
 * corner rays intersected with the floor plane (y = 0).
 *
 * Two facts make the naive version wrong. First, at /live's near-level camera
 * limit (maxPolarAngle ≈ 88°) the top of a 50° frustum points ABOVE the horizon,
 * so those rays never meet the floor — they must be clamped, not dropped, or the
 * shape collapses to a line. Second, even a ray that does descend can land
 * kilometres away; clamping at the map's own diagonal keeps the indicator inside
 * the map it is drawn on.
 *
 * Pure and three.js-free on purpose: the caller (inside the Canvas) supplies
 * normalized world-space ray directions, which is the only part that needs a
 * camera. That keeps this unit-testable with hand-built rays.
 */

export type Vec3 = readonly [number, number, number];

/** NDC corner order the reporter must use: BL, BR, TR, TL — a valid ring. */
export const NDC_CORNERS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1], [1, -1], [1, 1], [-1, 1],
];

/**
 * Where a ray meets the floor, in world (X, Z) metres. `dir` must be normalized
 * so `maxDistanceM` is metres. A ray that is level or rising (looking at or above
 * the horizon) is clamped at `maxDistanceM` along its own direction — the honest
 * reading of "the view goes that way and off the map".
 */
export function floorHit(origin: Vec3, dir: Vec3, maxDistanceM: number): [number, number] {
  const t = dir[1] < -1e-6 ? -origin[1] / dir[1] : Infinity;
  // Negative t means the floor is BEHIND a camera below the plane; 0 is the
  // correct answer there (straight down from the eye), never a backwards ray.
  const tc = Math.min(Math.max(t, 0), maxDistanceM);
  return [origin[0] + dir[0] * tc, origin[2] + dir[2] * tc];
}

/** The view's floor footprint as a 4-point ring in world (X, Z) metres. */
export function viewFootprint(
  origin: Vec3, dirs: ReadonlyArray<Vec3>, maxDistanceM: number,
): [number, number][] {
  return dirs.map((d) => floorHit(origin, d, maxDistanceM));
}

// ---------------------------------------------------------------------------
// Scene → panel handoff.
//
// A module singleton rather than a store: this changes every frame the camera
// moves, and no React component should re-render on it. The minimap reads it in
// a rAF loop and repaints only when `version` moved, so an idle camera costs
// nothing. `publishViewRays` is a no-op when the pose is unchanged, which is what
// makes that check work.
// ---------------------------------------------------------------------------

export interface ViewRays {
  origin: Vec3;
  dirs: Vec3[];
  version: number;
}

let current: ViewRays | null = null;

export function publishViewRays(origin: Vec3, dirs: Vec3[]): void {
  current = { origin, dirs, version: (current?.version ?? 0) + 1 };
}

export function readViewRays(): ViewRays | null {
  return current;
}

/** Drop the published pose — the scene unmounted, so the indicator is stale. */
export function clearViewRays(): void {
  current = null;
}
